import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Users, Mail, Phone, Calendar, Clock, MapPin, Edit, Trash2, Plus, Download } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/ui/use-toast';
import { Event, CustomerEventRegistration } from '@/types/index';
import { format } from 'date-fns';

interface CustomerEventRegistrationProps {
  event: Event;
  onClose: () => void;
}

const CustomerEventRegistrationManager: React.FC<CustomerEventRegistrationProps> = ({ event, onClose }) => {
  const [registrations, setRegistrations] = useState<CustomerEventRegistration[]>([]);
  const [loading, setLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingRegistration, setEditingRegistration] = useState<CustomerEventRegistration | null>(null);
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    notes: '',
    dietary_preferences: '',
    emergency_contact: '',
  });

  useEffect(() => {
    fetchRegistrations();
  }, [event.id]);

  const fetchRegistrations = async () => {
    try {
      const { data, error } = await supabase
        .from('customer_event_registrations')
        .select('*')
        .eq('event_id', event.id)
        .order('registration_date', { ascending: true });

      if (error) throw error;
      setRegistrations(data || []);
    } catch (error) {
      console.error('Error fetching registrations:', error);
      toast.error("Failed to load registrations. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const registrationData = {
        ...formData,
        event_id: event.id,
        attendance_status: 'registered' as const,
      };

      if (editingRegistration) {
        const { error } = await supabase
          .from('customer_event_registrations')
          .update(registrationData)
          .eq('id', editingRegistration.id);

        if (error) throw error;
        toast.success("Registration updated successfully!");
      } else {
        const { error } = await supabase
          .from('customer_event_registrations')
          .insert([registrationData]);

        if (error) throw error;
        toast.success("Customer registered successfully!");
      }

      setIsFormOpen(false);
      setEditingRegistration(null);
      setFormData({
        customer_name: '',
        customer_email: '',
        customer_phone: '',
        notes: '',
        dietary_preferences: '',
        emergency_contact: '',
      });
      await fetchRegistrations();
    } catch (error) {
      console.error('Error saving registration:', error);
      toast.error("Failed to save registration. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (registration: CustomerEventRegistration) => {
    setEditingRegistration(registration);
    setFormData({
      customer_name: registration.customer_name,
      customer_email: registration.customer_email,
      customer_phone: registration.customer_phone || '',
      notes: registration.notes || '',
      dietary_preferences: registration.dietary_preferences || '',
      emergency_contact: registration.emergency_contact || '',
    });
    setIsFormOpen(true);
  };

  const handleDelete = async (registrationId: string) => {
    if (!confirm('Are you sure you want to delete this registration?')) return;

    try {
      const { error } = await supabase
        .from('customer_event_registrations')
        .delete()
        .eq('id', registrationId);

      if (error) throw error;
      toast.success("Registration deleted successfully!");
      await fetchRegistrations();
    } catch (error) {
      console.error('Error deleting registration:', error);
      toast.error("Failed to delete registration. Please try again.");
    }
  };

  const updateAttendanceStatus = async (registrationId: string, status: string) => {
    try {
      const { error } = await supabase
        .from('customer_event_registrations')
        .update({ attendance_status: status })
        .eq('id', registrationId);

      if (error) throw error;
      toast.success("Attendance status updated!");
      await fetchRegistrations();
    } catch (error) {
      console.error('Error updating attendance:', error);
      toast.error("Failed to update attendance status.");
    }
  };

  const exportRegistrations = () => {
    const csvContent = [
      ['Name', 'Email', 'Phone', 'Registration Date', 'Status', 'Dietary Preferences', 'Notes'].join(','),
      ...registrations.map(reg => [
        reg.customer_name,
        reg.customer_email,
        reg.customer_phone || '',
        format(new Date(reg.registration_date), 'yyyy-MM-dd HH:mm'),
        reg.attendance_status,
        reg.dietary_preferences || '',
        reg.notes || ''
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${event.name}-registrations.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed': return 'bg-green-100 text-green-800';
      case 'attended': return 'bg-blue-100 text-blue-800';
      case 'no_show': return 'bg-red-100 text-red-800';
      case 'cancelled': return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
      default: return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
    }
  };

  if (loading && registrations.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Customer Registrations - {event.name}
          </DialogTitle>
          <DialogDescription>
            <div className="flex items-center gap-4 text-sm text-gray-600 mt-2">
              <div className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                {format(new Date(event.event_date), 'PPP')}
              </div>
              <div className="flex items-center gap-1">
                <Clock className="h-4 w-4" />
                {event.start_time}
              </div>
              {event.location && (
                <div className="flex items-center gap-1">
                  <MapPin className="h-4 w-4" />
                  {event.location}
                </div>
              )}
            </div>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Header with stats and actions */}
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <Badge variant="outline">
                {registrations.length} registrations
                {event.max_external_attendees && ` / ${event.max_external_attendees} limit`}
              </Badge>
              <Badge className="bg-green-100 text-green-800">
                {registrations.filter(r => r.attendance_status === 'confirmed').length} confirmed
              </Badge>
              <Badge className="bg-blue-100 text-blue-800">
                {registrations.filter(r => r.attendance_status === 'attended').length} attended
              </Badge>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={exportRegistrations}
                disabled={registrations.length === 0}
              >
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </Button>
              <Button
                onClick={() => setIsFormOpen(true)}
                size="sm"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Registration
              </Button>
            </div>
          </div>

          {/* Registrations list */}
          {registrations.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <Users className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                <h3 className="text-lg font-semibold mb-2">No registrations yet</h3>
                <p className="text-gray-600 mb-4">Add the first customer registration to get started.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-3 max-h-96 overflow-y-auto">
              {registrations.map((registration) => (
                <Card key={registration.id}>
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h4 className="font-medium text-lg">{registration.customer_name}</h4>
                          <Badge className={getStatusColor(registration.attendance_status)}>
                            {registration.attendance_status.replace('_', ' ')}
                          </Badge>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-600">
                          <div className="flex items-center gap-2">
                            <Mail className="h-4 w-4" />
                            {registration.customer_email}
                          </div>
                          {registration.customer_phone && (
                            <div className="flex items-center gap-2">
                              <Phone className="h-4 w-4" />
                              {registration.customer_phone}
                            </div>
                          )}
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4" />
                            Registered: {format(new Date(registration.registration_date), 'PPp')}
                          </div>
                        </div>
                        {registration.dietary_preferences && (
                          <p className="text-sm text-gray-600 mt-2">
                            <strong>Dietary:</strong> {registration.dietary_preferences}
                          </p>
                        )}
                        {registration.notes && (
                          <p className="text-sm text-gray-600 mt-1">
                            <strong>Notes:</strong> {registration.notes}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {registration.attendance_status === 'registered' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => updateAttendanceStatus(registration.id, 'confirmed')}
                          >
                            Confirm
                          </Button>
                        )}
                        {(registration.attendance_status === 'registered' || registration.attendance_status === 'confirmed') && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => updateAttendanceStatus(registration.id, 'attended')}
                          >
                            Mark Attended
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(registration)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(registration.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>

        {/* Registration Form Dialog */}
        {isFormOpen && (
          <Dialog open={isFormOpen} onOpenChange={() => {
            setIsFormOpen(false);
            setEditingRegistration(null);
            setFormData({
              customer_name: '',
              customer_email: '',
              customer_phone: '',
              notes: '',
              dietary_preferences: '',
              emergency_contact: '',
            });
          }}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>
                  {editingRegistration ? 'Edit Registration' : 'Add Customer Registration'}
                </DialogTitle>
              </DialogHeader>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="customer_name">Customer Name *</Label>
                  <Input
                    id="customer_name"
                    value={formData.customer_name}
                    onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="customer_email">Email *</Label>
                  <Input
                    id="customer_email"
                    type="email"
                    value={formData.customer_email}
                    onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="customer_phone">Phone Number</Label>
                  <Input
                    id="customer_phone"
                    type="tel"
                    value={formData.customer_phone}
                    onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                  />
                </div>

                <div>
                  <Label htmlFor="dietary_preferences">Dietary Preferences</Label>
                  <Input
                    id="dietary_preferences"
                    value={formData.dietary_preferences}
                    onChange={(e) => setFormData({ ...formData, dietary_preferences: e.target.value })}
                    placeholder="e.g., Vegetarian, Gluten-free, etc."
                  />
                </div>

                <div>
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={2}
                  />
                </div>

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setIsFormOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={loading}>
                    {loading ? 'Saving...' : (editingRegistration ? 'Update' : 'Add Registration')}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CustomerEventRegistrationManager;